import React, { useState } from 'react';
import axios from 'axios';
import { ArrowLeft, UserPlus, User, Lock, Mail, Building, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { COUNTRY_CODES, findCountry } from '../lib/countries';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = ({ onNavigateBack, onNavigateToSignIn, onRegisterSuccess }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    company: '',
    password: '',
    confirmPassword: '',
    whatsappCountryCode: '1',
    whatsappNumber: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.username || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Password match validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Password strength validation
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (!/^[0-9]{6,15}$/.test(formData.whatsappNumber.trim())) {
      setError('Please enter a valid WhatsApp number (digits only, 6–15 chars).');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/register`, {
        username: formData.username,
        email: formData.email,
        company: formData.company,
        password: formData.password,
        whatsapp_country_code: formData.whatsappCountryCode,
        whatsapp_number: formData.whatsappNumber.trim()
      });

      setLoading(false);
      onRegisterSuccess(response.data); // Pass full user data
    } catch (error) {
      console.error('Registration error:', error);
      setError(error.response?.data?.detail || 'Failed to register. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3A5F] via-[#2C5282] to-[#1E3A5F] page-transition">
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_dc1c0b21-5011-47e1-a19d-11de6678deed/artifacts/55cbyjpt_Logo.png" 
                alt="PriceValuator Logo" 
                className="h-48 object-contain"
              />
              <Button 
                variant="ghost" 
                onClick={onNavigateBack}
                className="text-[#1E3A5F] hover:bg-gray-100 rounded-full px-4 py-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Register Form */}
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <Card className="rounded-2xl border-2 border-cyan-400/30 shadow-2xl bg-white/95 backdrop-blur-lg">
            <CardHeader className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-t-2xl pb-8">
              <CardTitle className="text-3xl text-center text-[#1E3A5F] flex flex-col items-center">
                <div className="bg-cyan-100 p-4 rounded-full mb-4">
                  <UserPlus className="w-8 h-8 text-cyan-600" />
                </div>
                Create Your Account
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              {error && (
                <Alert className="mb-6 bg-red-50 border-red-200">
                  <AlertDescription className="text-red-800">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Username <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Choose a username"
                    className="w-full"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@example.com"
                    className="w-full"
                    required
                  />
                </div>

                {/* Company */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building className="w-4 h-4 inline mr-2" />
                    Company <span className="text-gray-400 text-xs">(Optional)</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    placeholder="Your company name"
                    className="w-full"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Password <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a password"
                    className="w-full"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Lock className="w-4 h-4 inline mr-2" />
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm your password"
                    className="w-full"
                    required
                  />
                </div>

                {/* WhatsApp */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MessageCircle className="w-4 h-4 inline mr-2" />
                    WhatsApp Number <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-[minmax(0,160px)_1fr] gap-2">
                    <select
                      data-testid="register-wa-country"
                      value={formData.whatsappCountryCode}
                      onChange={(e) => setFormData({ ...formData, whatsappCountryCode: e.target.value })}
                      className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={`${c.iso}-${c.code}`} value={c.code}>
                          {c.flag} {c.name} ({c.dial})
                        </option>
                      ))}
                    </select>
                    <div className="flex items-stretch">
                      <span
                        aria-hidden="true"
                        className="inline-flex items-center rounded-l-md border border-r-0 border-slate-300 bg-slate-50 px-3 text-sm text-slate-600"
                      >
                        {findCountry(formData.whatsappCountryCode).dial}
                      </span>
                      <Input
                        data-testid="register-wa-number"
                        type="tel"
                        inputMode="numeric"
                        placeholder="e.g. 5551234567"
                        value={formData.whatsappNumber}
                        onChange={(e) => setFormData({ ...formData, whatsappNumber: e.target.value.replace(/[^0-9]/g, '') })}
                        className="rounded-l-none"
                        required
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Used to verify your account at every sign-in via WhatsApp OTP.
                  </p>
                </div>

                {/* Register Button */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white rounded-full py-3 font-semibold text-lg"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>

              {/* Sign In Link */}
              <div className="mt-8 text-center">
                <p className="text-gray-600 mb-3">Already have an account?</p>
                <Button
                  onClick={onNavigateToSignIn}
                  variant="outline"
                  className="rounded-full border-cyan-400 text-cyan-600 hover:bg-cyan-50 px-6 py-2"
                >
                  Sign In
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
